import {
  ApprovalRules,
  PolicyTimeTrigger,
  ReturnApprovalMode,
} from 'types/policy';

type PolicySettingsValidationInput = {
  page?: 'policy' | 'returns' | 'approval';
  returnWindow: number;
  shipmentDeadlineDays: number;
  shipmentReminderDaysInput: string;
  windowStart: PolicyTimeTrigger;
  approvalRules: Omit<
    ApprovalRules,
    'maxReturnValue' | 'maxItemsInReturn' | 'maxPriorReturns'
  > & {
    maxReturnValue?: number | null;
    maxItemsInReturn?: number | null;
    maxPriorReturns?: number | null;
  };
  eligibleApprovalMode: ReturnApprovalMode;
  flaggedApprovalMode: ReturnApprovalMode;
};

export const parseShipmentReminderDays = (value: string): number[] | null => {
  const trimmedValue = value.trim();
  if (!trimmedValue) return [];

  const parts = trimmedValue.split(',').map((part) => part.trim());
  if (parts.some((part) => !/^\d+$/.test(part))) return null;

  return Array.from(new Set(parts.map(Number)));
};

type OptionalNumber = number | null | undefined;

const isClearedNumber = (value: OptionalNumber): value is null | undefined =>
  value === null || value === undefined;

const getNonNegativeNumberError = (value: OptionalNumber, label: string) => {
  if (isClearedNumber(value)) return null;

  if (!Number.isFinite(value) || value < 0) {
    return `${label} must be 0 or greater.`;
  }

  return null;
};

const getNonNegativeIntegerError = (value: OptionalNumber, label: string) => {
  if (isClearedNumber(value)) return null;

  const numberError = getNonNegativeNumberError(value, label);
  if (numberError) return numberError;
  if (!Number.isInteger(value)) return `${label} must be a whole number.`;

  return null;
};

export const getPolicySettingsValidationError = ({
  page,
  returnWindow,
  shipmentDeadlineDays,
  shipmentReminderDaysInput,
  approvalRules,
}: PolicySettingsValidationInput): string | null => {
  if (page === 'returns') return null;
  if (page !== 'approval') {
    if (!Number.isInteger(returnWindow) || returnWindow <= 0) {
      return 'Return window must be a positive whole number.';
    }

    if (!Number.isInteger(shipmentDeadlineDays) || shipmentDeadlineDays <= 0) {
      return 'Ship-back deadline must be a positive whole number.';
    }

    const shipmentReminderDays = parseShipmentReminderDays(
      shipmentReminderDaysInput,
    );
    if (!shipmentReminderDays) {
      return 'Reminder days must be comma-separated whole numbers.';
    }
    if (shipmentReminderDays.some((day) => day > shipmentDeadlineDays)) {
      return 'Reminder days cannot exceed the ship-back deadline.';
    }
  }

  if (page === 'policy') return null;

  return (
    getNonNegativeNumberError(approvalRules.maxReturnValue, 'Return value') ||
    getNonNegativeIntegerError(
      approvalRules.maxItemsInReturn,
      'Item quantity',
    ) ||
    getNonNegativeIntegerError(
      approvalRules.maxPriorReturns,
      'Customer return history',
    )
  );
};
